// live — THE tab's one live client (ADR-074 §1), wired to the app's singletons:
// the glance rides `apiClient` (an ordinary authenticated project request — a
// revoked session, a suspended project, a removed member surface within one
// rhythm through the interceptor, with no code here), the look-agains ride the
// `queryClient`, the report lands in the live store, and `apiClient` tells it of
// every successful write of the person's own.
//
// `RootLayout` starts it on a project URL and stops it outside one; a live hook
// feeds it through `liveGet`. A page never reads a token and never sets a timer;
// the ONE thing a page may ask is `isLiveReRead` — so an automatic look-again
// stays quiet.

import type { QueryKey } from '@tanstack/react-query';
import { apiClient, setOnProjectWrite } from '../apiClient.js';
import { queryClient } from '../queryClient.js';
import { codeOf, retryAfterOf, statusOf } from '../refusal.js';
import { ME_QUERY_KEY } from '../../hooks/useMe.js';
import { useLiveStore } from '../../stores/liveStore.js';
import { createLiveClient, type GlanceResult } from './liveClient.js';

/** The glance — the tab's own question (`quietBusy`: a BUSY answer raises no
 *  toast; the live client paces itself by the hint). A refusal is read by its
 *  shape (lib/refusal.ts): the body's CODE tells a BUSY answer from a proxy's
 *  own 503; the hint from the body first, then the header. Exported for its test. */
export async function glance(projectId: string, signal: AbortSignal): Promise<GlanceResult> {
  try {
    const answer = await apiClient.get<unknown>(`/projects/${projectId}/glance`, { signal, quietBusy: true });
    return { ok: true, data: answer.data };
  } catch (error) {
    return { ok: false, status: statusOf(error), retryAfterS: retryAfterOf(error), code: codeOf(error) };
  }
}

export const liveClient = createLiveClient({
  queryClient,
  glance,
  meQueryKey: ME_QUERY_KEY,
  report: (next) => useLiveStore.setState(next),
});

setOnProjectWrite((projectId) => liveClient.noteOwnWrite(projectId));

/** Is this question being re-asked by the live client ITSELF right now? For one
 *  use: the "refreshing" sign of a list is for a refetch the PERSON caused (a
 *  filter, a search, a save) — an automatic look-again shows nothing but its
 *  result (ADR-075 §8). Read at render: the re-read's start and end both
 *  re-render the query's observers. */
export const isLiveReRead = (queryKey: QueryKey): boolean => liveClient.isLookingAgain(queryKey);
